import { ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logoMark from '../../assets/images/Logos/kole-medical-emblem.svg';
import PrototypeNotice from "../EventsSection/PrototypeNotice";
import { AdminSession } from "../../webinars/types";
import { webinarRepository } from "../../webinars/repository";

// The admin's own layout: no public footer, and its own navigation.
//   Desktop: header (brand, Webinars page, Main site, account and Sign out), then a sticky bar
//            linking the dashboard's sections, highlighting the one in view.
//   Phones:  a compact sticky top bar (brand and ☰); the ☰ drawer holds the sections, the
//            signed-in account with Sign out, and the other pages.

export interface AdminSection {
  /** The id of the section's element on the page */
  id: string;
  label: string;
}

interface AdminShellProps {
  session?: AdminSession | null;
  /** The dashboard's sections, once signed in */
  sections?: AdminSection[];
  children: ReactNode;
}

/** The section nearest the top of the screen, below the sticky bar */
function useActiveSection(ids: string[]) {
  const [active, setActive] = useState(ids[0] ?? "");
  const key = ids.join("|");

  useEffect(() => {
    if (!ids.length) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      let current = ids[0];
      for (const id of ids) {
        const element = document.getElementById(id);
        if (element && element.getBoundingClientRect().top <= 140) current = id;
      }
      // Scrolled to the very end: the last section, even if it's too short to reach the top
      const last = document.getElementById(ids[ids.length - 1]);
      if (last && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = ids[ids.length - 1];
      setActive(current);
    };
    const onScroll = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}

/** Scrolls to a section and moves keyboard focus there, without changing the address */
function goToSection(id: string) {
  const element = document.getElementById(id);
  if (!element) return;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  element.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  if (!element.hasAttribute("tabindex")) element.setAttribute("tabindex", "-1");
  element.focus({ preventScroll: true });
}

export default function AdminShell({ session, sections, children }: AdminShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const active = useActiveSection((sections ?? []).map(s => s.id));
  const canSignOut = Boolean(session) && webinarRepository.backend === "supabase";

  useEffect(() => setDrawerOpen(false), [pathname]);

  // The drawer: focus its first link on open; Escape closes it and returns to ☰
  useEffect(() => {
    if (!drawerOpen) return;
    drawerRef.current?.querySelector<HTMLElement>("a, button:not(.wa-drawer-close)")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const signOut = () => {
    setDrawerOpen(false);
    webinarRepository.signOut();
  };

  return (
    <div className={`wa${sections ? " wa--sections" : ""}`}>
      <PrototypeNotice variant="admin" />
      <header className="wa-header">
        <div className="km-container wa-header-inner">
          <Link to="/admin" className="wa-brand">
            <img src={logoMark} alt="" width={36} height={36} />
            <span>Kole Medical</span>
            <span className="wa-brand-product">Webinar admin</span>
          </Link>
          <nav className="wa-header-links" aria-label="Admin">
            <Link to="/events">Webinars page</Link>
            <Link to="/">Main site</Link>
            {canSignOut && (
              <button type="button" className="wa-link wa-signout" onClick={signOut}>
                Sign out<span className="wa-signout-email"> {session!.email}</span>
              </button>
            )}
          </nav>
          <button
            ref={toggleRef}
            type="button"
            className="wa-drawer-toggle"
            aria-expanded={drawerOpen}
            aria-controls="wa-drawer"
            aria-label="Open the admin menu"
            onClick={() => setDrawerOpen(true)}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {sections && (
        <nav className="wa-sectionbar" aria-label="Dashboard sections">
          <div className="km-container wa-sectionbar-inner">
            {sections.map(section => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className={active === section.id ? "is-active" : undefined}
                aria-current={active === section.id ? "location" : undefined}
                onClick={e => { e.preventDefault(); goToSection(section.id); }}
              >
                {section.label}
              </a>
            ))}
          </div>
        </nav>
      )}

      {drawerOpen && (
        <div className="wa-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <div
            ref={drawerRef}
            id="wa-drawer"
            className="wa-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Admin menu"
            onClick={e => e.stopPropagation()}
          >
            <div className="wa-drawer-head">
              <strong>Menu</strong>
              <button type="button" className="wa-drawer-close" aria-label="Close the admin menu" onClick={() => { setDrawerOpen(false); toggleRef.current?.focus(); }}>×</button>
            </div>

            {sections && (
              <nav aria-label="Dashboard sections">
                <p className="wa-drawer-label">Dashboard</p>
                {sections.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`wa-drawer-link${active === section.id ? " is-active" : ""}`}
                    aria-current={active === section.id ? "location" : undefined}
                    onClick={e => { e.preventDefault(); setDrawerOpen(false); goToSection(section.id); }}
                  >
                    {section.label}
                  </a>
                ))}
              </nav>
            )}

            {canSignOut && (
              <div className="wa-drawer-account">
                <p className="wa-drawer-label">Signed in as</p>
                <p className="wa-drawer-email">{session!.email}</p>
                <button type="button" className="km-btn km-btn--outline km-btn--block" onClick={signOut}>Sign out</button>
              </div>
            )}

            <nav aria-label="Other pages">
              <p className="wa-drawer-label">Other pages</p>
              <Link className="wa-drawer-link" to="/events">Webinars page</Link>
              <Link className="wa-drawer-link" to="/">Kole Medical main site</Link>
            </nav>
          </div>
        </div>
      )}

      <main className="km-container wa-main">{children}</main>
    </div>
  );
}
